import type { ProfileAnswers } from "../domain/answers";
import { mediaIsDurable } from "../media/uploadService";
import type { FlowAnswers, StepId } from "./steps";
import {
  PHOTO_MAX,
  PHOTO_MIN,
  TEXT_MIN,
  rulesAccepted,
  scalesComplete,
} from "./steps";

/**
 * Hard gates and field validation for each onboarding step.
 * Keep in sync with AGENTS.md mandatory questions.
 */
export function canProceed(
  stepId: StepId,
  answers: FlowAnswers,
  _profile: ProfileAnswers
): boolean {
  switch (stepId) {
    case "faithConfirm":
      return answers.isChristian === true;

    case "communityRules":
      return rulesAccepted(answers);

    case "intent":
      return Boolean(answers.intent);

    case "account":
      return answers.email.includes("@") && answers.password.length >= 8;

    case "verifyId":
      return (
        answers.governmentId?.reviewStatus === "pending_review" &&
        mediaIsDurable(answers.governmentId)
      );

    case "verifyVideo":
      return (
        answers.confirmationVideo?.reviewStatus === "pending_review" &&
        mediaIsDurable(answers.confirmationVideo)
      );

    case "verifyPhotos":
      return (
        answers.profilePhotos.length >= PHOTO_MIN &&
        answers.profilePhotos.length <= PHOTO_MAX &&
        answers.profilePhotos.every(
          (p) => p.reviewStatus === "pending_review" && mediaIsDurable(p)
        )
      );

    case "whyChristian":
      return answers.whyChristian.trim().length >= TEXT_MIN.whyChristian;

    case "valueScales":
      return scalesComplete(answers);

    case "aboutYou":
      return answers.aboutYou.trim().length >= TEXT_MIN.aboutYou;

    case "lookingFor":
      return answers.lookingFor.trim().length >= TEXT_MIN.lookingFor;

    case "loveReady":
      return answers.loveReady === true;

    case "lifestyle":
      return (
        answers.hobbies.trim().length >= TEXT_MIN.hobbies &&
        answers.profession.trim().length >= TEXT_MIN.profession
      );

    case "faithDepth":
    case "character":
    case "history":
    case "appearance":
    case "homeAndLand":
    case "family":
    case "ancestry":
    case "interests":
    case "reflection":
      return true;

    case "profile":
      return (
        answers.firstName.trim().length >= TEXT_MIN.firstName &&
        Number(answers.age) >= 18 &&
        answers.city.trim().length >= TEXT_MIN.city &&
        (answers.gender === "male" || answers.gender === "female")
      );

    default:
      return true;
  }
}
